import dynamic from 'next/dynamic'
import classNames from 'classnames'
import { useQuery } from '@apollo/client'
import { FC } from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import 'react-photo-view/dist/react-photo-view.css'
import About from 'components/event/about'
import { useStore } from 'components/contexts/event-page-filters'
import { getEventMetaData, getEventSchema } from 'utils/seo'
import { useQueryParams, useScreenClass } from 'hooks'
import { getIdFromSlug, getImageUrl } from 'utils'
import { GetEventAuctionByIdQuery, TevoTicketsQuery, TevoTicketsQueryVariables } from 'gql'
import { SlideShowGallery, MetaTags } from '~shared'
import { Flex, NoData, Spinner } from '~ui'
import { ArtistMeta } from 'types/artist'

import { TEVO_TICKETS } from 'gql/artwork/queries'
import styles from './Info.module.scss'

const SeatMap = dynamic(() => import('components/shared/SeatMap'), { ssr: false })

const SHOW_IMAGE_PLACEHOLDER_IDS = [
  393, 537, 19024, 19025, 19026, 19027, 19028, 19029, 19030, 19031, 19032,
]

const EventInfo: FC<{
  event: GetEventAuctionByIdQuery['findArtwork']
  artistMeta?: ArtistMeta
}> = ({ event, artistMeta }) => {
  const {
    query: { slug },
  } = useQueryParams()
  const { vip } = useStore()
  const { isMobile, isDesktop } = useScreenClass()

  const images = event?.images.edges
    .map(({ node }) => ({
      url: getImageUrl(node, 820, 540),
      key: node.id,
    }))
    .reverse()

  const thumbnails = event?.images.edges
    .map(({ node }) => ({
      url: getImageUrl(node, 120, 80),
      key: node.id,
    }))
    .reverse()

  const imageSrc = event?.artworkOrTourOrArtistImage

  const { data: tevoTickets, loading: tevoTicketsLoading } = useQuery<
    TevoTicketsQuery,
    TevoTicketsQueryVariables
  >(TEVO_TICKETS, {
    variables: { artworkPk: parseInt(getIdFromSlug(slug), 10) },
    skip:
      vip ||
      event.origin !== 'tevo' ||
      event.eventExtendedStatus === 'canceled' ||
      artistMeta?.hideTEvoTickets,
  })

  const showACLImage = SHOW_IMAGE_PLACEHOLDER_IDS.includes(Number(event?.pk))

  const showTevoTickets = !vip && event.origin === 'tevo' && tevoTickets
  const showTevoTicketsLoading = !vip && event.origin === 'tevo' && tevoTicketsLoading

  const showPackages = vip || artistMeta?.hideTEvoTickets
  const defaultImage = 'https://img.granted.co/1659713157124-acl-lineup-2022.png'

  return (
    <>
      <MetaTags
        {...getEventMetaData({
          ...event,
          vip,
          imageSrc,
        })}
        schema={getEventSchema({ ...event, imageSrc })}
      >
        <meta name="robots" content="noindex, nofollow" />
      </MetaTags>
      <div className={styles.info}>
        {event.eventExtendedStatus === 'canceled' ? (
          <Flex alignItems="center" justifyContent="center" height="100%">
            <NoData description="Event was canceled" />
          </Flex>
        ) : (
          <>
            {showPackages && (
              <div className={styles.content}>
                <Flex alignItems="center" justifyContent="center" height="100%">
                  <div className={styles['gallery-wrapper']}>
                    {images?.length > 1 ? (
                      <SlideShowGallery
                        images={images}
                        thumbnail={{
                          hide: isMobile,
                          images: thumbnails,
                          size: {
                            width: 120,
                            height: 80,
                          },
                        }}
                      />
                    ) : (
                      <PhotoProvider maskOpacity={0.9}>
                        <PhotoView src={getImageUrl(imageSrc)}>
                          <img
                            src={getImageUrl(imageSrc)}
                            className={styles.image}
                            alt={event.title}
                            style={{ cursor: 'zoom-in' }}
                          />
                        </PhotoView>
                      </PhotoProvider>
                    )}
                  </div>
                </Flex>
                <About />
              </div>
            )}

            {showTevoTicketsLoading && (
              <Flex alignItems="center" width="100%" height="100%" justifyContent="center">
                <Spinner center />
              </Flex>
            )}

            {showTevoTickets && (
              <div className={classNames(styles.seatmap)}>
                <SeatMap
                  configurationId={tevoTickets.findArtwork.tevoSeatingChartConfiguration.id}
                  ticketGroups={tevoTickets.findArtwork.tevoTicketGroups}
                  venueId={event.venue.tevoId}
                  customPlaceholder={
                    showACLImage && (
                      <div style={{ overflowY: 'scroll', height: '100%' }}>
                        {isDesktop ? (
                          <img src={defaultImage} className={styles.image} alt={event.title} />
                        ) : (
                          <PhotoProvider>
                            <PhotoView src={defaultImage}>
                              <img
                                src={defaultImage}
                                className={styles.image}
                                alt={event.title}
                                style={{ cursor: 'zoom-in' }}
                              />
                            </PhotoView>
                          </PhotoProvider>
                        )}
                      </div>
                    )
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default EventInfo
